import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingCheck } from '@/hooks/useOnboardingCheck';
import { useAuth } from '@/contexts/AuthContext';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

export const OnboardingGuard = ({ children }: OnboardingGuardProps) => {
  const { user, loading: authLoading, userRoles } = useAuth();
  const { hasChildren, isLoading, needsOnboarding } = useOnboardingCheck();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || isLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    // Super admins bypass onboarding
    if (userRoles.includes('super_admin')) return;

    // Parents must add at least one child
    if (userRoles.includes('parent') && needsOnboarding) {
      navigate('/register/individual');
    }
  }, [user, authLoading, isLoading, needsOnboarding, navigate, userRoles]);

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
